import { createClient } from '@/lib/supabase/client';

export interface LandingPageContent {
  id: string;
  pageKey: 'drivers' | 'users' | 'retailers';
  sectionKey: 'hero' | 'benefits' | 'requirements' | 'howItWorks' | 'earnings' | 'whyChoose';
  content: any;
  displayOrder: number;
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LandingPageContentInput {
  pageKey: 'drivers' | 'users' | 'retailers';
  sectionKey: 'hero' | 'benefits' | 'requirements' | 'howItWorks' | 'earnings' | 'whyChoose';
  content: any;
  displayOrder: number;
  isActive: boolean;
}

class LandingPageService {
  async getAll(): Promise<LandingPageContent[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('landing_page_content')
      .select('*')
      .order('page_key', { ascending: true })
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getByPageKey(pageKey: 'drivers' | 'users' | 'retailers'): Promise<LandingPageContent[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('landing_page_content')
      .select('*')
      .eq('page_key', pageKey)
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getByPageAndSection(pageKey: 'drivers' | 'users' | 'retailers', sectionKey: string): Promise<LandingPageContent | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('landing_page_content')
      .select('*')
      .eq('page_key', pageKey)
      .eq('section_key', sectionKey)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }
    
    return data ? this.mapFromDb(data) : null;
  }

  async upsert(input: LandingPageContentInput): Promise<LandingPageContent | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('landing_page_content')
      .upsert([{
        page_key: input.pageKey,
        section_key: input.sectionKey,
        content: input.content,
        display_order: input.displayOrder,
        is_active: input.isActive,
        created_by: user.id,
      }], {
        onConflict: 'page_key,section_key',
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('landing_page_content')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  async updateDisplayOrder(id: string, displayOrder: number): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('landing_page_content')
      .update({ display_order: displayOrder })
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  async toggleActive(id: string, isActive: boolean): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('landing_page_content')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  private mapFromDb(data: any): LandingPageContent {
    return {
      id: data.id,
      pageKey: data.page_key,
      sectionKey: data.section_key,
      content: data.content,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const landingPageService = new LandingPageService();
